<script setup lang="ts">
/**
 * Строка формы участника нежелательного события (НС) или аудита (если передан пропс).
 *
 * Назначение:
 * - Отображение и редактирование данных одного участника НС.
 * - Поддержка редактирования ФИО, даты рождения и телефона.
 * - Эмиссия события удаления участника.
 *
 * Особенности:
 * - Работа напрямую с состоянием currentAdverseEvent из apiStore.
 * - Форматирование даты между API-форматом и пользовательским представлением.
 * - Адаптивное поведение в зависимости от размера экрана.
 */

import { computed, inject, ref } from 'vue'
import { useDisplay } from 'vuetify/framework'
import { VIconBtn } from 'vuetify/labs/VIconBtn'
import DateMaskInput from '@/components/createdUi/DateMaskInput.vue'
import EmployeeSelectionModal from '@/components/createdUi/EmployeeSelectionModal.vue'
import { useApiStore } from '@/modules/apiStore/stores/apiStore'
import { useAuditGroupStore } from '@/modules/audits/stores/useAuditGroupStore/useAuditGroupStore'
import CollapsibleCell from '@/components/createdUi/CollapsibleCell.vue'
import UniversalTreeMultiSelect from '@/components/createdUi/UniversalTreeMultiSelect.vue'
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue'

import type { IParticipantFormRowProps } from '@/modules/apiStore/types/adverse-events-participants/IParticipantFormRowProps'
import type { IEmployeeTreeItem } from '@/modules/apiStore/types/employees/IEmployeeTree'

/**
 * Пропсы строки участника.
 * Содержат идентификатор и значения полей участника.
 */
const props = defineProps<IParticipantFormRowProps & {
  isAuditParticipant?: boolean
  isPpuParticipant?: boolean
  hideParticipantTypeLabel?: boolean
  role?: string
  criteria?: { id: string; label: string }[]
  points?: { id: string; label: string }[]
  employee_id?: string | null
}>()
/**
 * Эмиттер событий компонента.
 */
const emit = defineEmits<Emits>()
const apiStore = useApiStore()
const auditGroupStore = useAuditGroupStore()

/**
 * Информация о текущем размере экрана.
 * Используется для адаптивной логики UI.
 */
const { xs } = useDisplay()

/**
 * Типизация событий, эмитируемых компонентом.
 */
interface Emits {
  (e: 'delete', value: string): void // Событие удаления участника
  (e: 'update-role', value: string): void // Обновление роли участника PPU
}

const auditorErrors = inject('auditorErrors', ref<Record<string, boolean>>({}))

// Флаг ошибки для конкретной строки
const hasCriterionError = computed(() => {
  return props.isAuditParticipant && !!auditorErrors.value[props.id]
})

const employeePosition = computed(() => {
  if (props.employee_position_name) {
    return props.employee_position_name
  }

  const targetId = props.isAuditParticipant ? props.id : props.employee_id
  if (!targetId) return ''

  if (apiStore.employees) {
    const findEmployee = (depts: any[]): any => {
      for (const dept of depts) {
        if (dept.employees) {
          const emp = dept.employees.find((e: any) => e.id === targetId)
          if (emp) return emp
        }
        if (dept.children) {
          const emp = findEmployee(dept.children)
          if (emp) return emp
        }
      }
      return null
    }
    const emp = findEmployee(apiStore.employees)
    if (emp) {
      return emp.position_name || emp.position?.name || ''
    }
  }
  return ''
})

const isCriteriaMenuOpen = ref(false)

const auditPoints = computed(() => props.points ?? props.criteria ?? [])

const selectedCriteriaIds = computed(() => {
  return auditPoints.value.map(c => c.id) || []
})

const onPointsChange = (items: { id: string | number; label: string }[]) => {
  if (!props.isAuditParticipant) return

  const auditor = auditGroupStore.currentGroup.group_assignments.find(a => a.employee.id === props.id)
  if (auditor) {
    auditor.points = items as { id: string; label: string }[]
  }
}

const criteriaDisplayText = computed(() => {
  if (!auditPoints.value.length) return ''
  return auditPoints.value.map(c => c.label.split(' ')[0]).join(', ')
})

const onClearCriteria = () => {
  onPointsChange([])
}

const canCopyPoints = computed(() => props.points !== undefined)
const copyPointsBtnColor = computed(() => auditPoints.value.length ? 'primary' : 'text-primary')

const copyPointsToOthers = () => {
  if (!canCopyPoints.value) return

  const source = auditPoints.value.map(point => ({ id: point.id, label: point.label }))

  auditGroupStore.currentGroup.group_assignments.forEach(assignment => {
    if (assignment.employee.id === props.id) return

    const currentPoints = assignment.points ?? []
    const existingIds = new Set(currentPoints.map(point => point.id))
    const added = source
      .filter(point => !existingIds.has(point.id))
      .map(point => ({ ...point }))

    assignment.points = [...currentPoints, ...added]
  })
}

// Ref на модалку выбора сотрудника (для замены участника по клику на ФИО)
const employeeModalRef = ref<InstanceType<typeof EmployeeSelectionModal> | null>(null)

// Обработчик выбора нового сотрудника для замены текущего участника
const onEmployeeReplace = (employee: IEmployeeTreeItem) => {
  const auditor = auditGroupStore.currentGroup.group_assignments.find(a => a.employee.id === props.id)
  if (!auditor) return

  auditor.employee = {
    id: employee.id,
    full_name: employee.full_name,
  }
}

/**
 * Инициирует удаление текущего участника.
 *
 * Назначение:
 * - Форматирует ФИО как инициалы "А.Б.В." при вводе.
 */
const handleInitialsInput = (val: string) => {
  const prevVal = props.full_name || ''

  if (val.length < prevVal.length) {
    const prevClean = prevVal.replace(/[^а-яА-ЯёЁ]/g, '')
    const newClean = prevClean.slice(0, -1).toUpperCase()

    let formatted = ''
    if (newClean.length > 0) formatted += `${newClean[0]}.`
    if (newClean.length > 1) formatted += `${newClean[1]}.`
    if (newClean.length > 2) formatted += `${newClean[2]}.`

    updateField('full_name', formatted)
    return
  }

  const clean = val.replace(/[^а-яА-ЯёЁ]/g, '').toUpperCase().slice(0, 3)
  let formatted = ''
  if (clean.length > 0) formatted += `${clean[0]}.`
  if (clean.length > 1) formatted += `${clean[1]}.`
  if (clean.length > 2) formatted += `${clean[2]}.`

  updateField('full_name', formatted)
}

/**
 * Назначение:
 * - Эмитит событие delete с идентификатором участника.
 *
 * @param emitValue Идентификатор участника.
 */
const showDeleteConfirm = ref(false)

const deleteConfirmText = computed(() =>
  props.full_name
    ? `Участник «${props.full_name}» будет удалён из группы`
    : 'Участник будет удалён из группы',
)

const deleteCurrentParticipant = (emitValue: string): void => {
  emit('delete', emitValue)
}

const onDeleteClick = () => {
  if (props.isAuditParticipant) {
    showDeleteConfirm.value = true
    return
  }

  deleteCurrentParticipant(props.id)
}

const onDeleteConfirm = (confirmed: boolean) => {
  showDeleteConfirm.value = false
  if (confirmed)
    deleteCurrentParticipant(props.id)
}

/**
 * Обновление конкретного поля участника.
 *
 * Логика:
 * - Ищет участника в currentAdverseEvent по идентификатору.
 * - Обновляет указанное поле новым значением.
 *
 * @param field Имя обновляемого поля.
 * @param value Новое значение поля.
 */
const updateField = <K extends 'full_name' | 'birth_date' | 'phone_number' | 'medical_record_number'>(
  field: K,
  value: string,
) => {
  // 1. ВЕТКА ДЛЯ АУДИТОВ
  if (props.isAuditParticipant) {
    // Ищем участника в сторе Аудитов по ID сотрудника
    const auditor = auditGroupStore.currentGroup.group_assignments.find(a => a.employee.id === props.id)

    if (auditor && auditor.employee) {
      (auditor.employee as any)[field] = value
    }
    return
  }

  const participant = apiStore.currentAdverseEvent.participants
    .find(p => p.id === props.id)

  if (!participant) return

  participant[field] = value
}

const isCellExpanded = ref(false)
</script>

<template>
  <div class="d-flex w-100" :class="xs ? 'flex-column gap-4' : 'align-center gap-2'">
    <!--
      Для аудиторов на десктопе блок пустой и из‑за gap-2 сдвигает ФИО вправо —
      рендерим его только для НС или для мобильной кнопки удаления.
    -->
    <div
      v-if="(!props.isAuditParticipant && !props.isPpuParticipant) || (xs && (props.show_delete_button || canCopyPoints))"
      class="d-flex"
      :class="{ 'justify-between w-100': xs }"
    >
      <span
        v-if="!props.isAuditParticipant && !props.isPpuParticipant"
        class="text-secondary "
        :style="{ minWidth: '90px' }"
      >
        {{ apiStore.participantTypeOptions.find(option => option.value === props.participant_type)?.label }}
      </span>

      <div
        v-if="xs && (props.show_delete_button || canCopyPoints)"
        class="d-flex align-center ga-3 ml-auto"
      >
        <VTooltip
          v-if="canCopyPoints"
          location="top"
          text="Скопировать предмет проверки во все поля"
        >
          <template #activator="{ props: tooltipProps }">
            <span v-bind="tooltipProps">
              <VIconBtn
                :rounded="false"
                :size="34"
                class="rounded-sm flex-grow-0 flex-shrink-0 copy-points-btn"
                :color="copyPointsBtnColor"
                icon="ri-checkbox-multiple-blank-line"
                variant="text"
                :disabled="!auditPoints.length"
                @click="copyPointsToOthers"
              />
            </span>
          </template>
        </VTooltip>
        <VIconBtn
          v-if="props.show_delete_button"
          :rounded="false"
          :size="34"
          class="rounded-sm flex-grow-0 flex-shrink-0"
          color="secondary"
          icon="ri-delete-bin-3-line"
          variant="text"
          @click="onDeleteClick"
        />
      </div>
    </div>

    <VTextField
      v-if="props.participant_type === 'patient' || props.participant_type === 'visitor'"
      :model-value="props.full_name"
      :disabled="readonly"
      class="flex-grow-1"
      label="ФИО (Инициалы)"
      density="comfortable"
      hide-details="auto"
      variant="outlined"
      placeholder="Ф.И.О."
      persistent-placeholder
      maxlength="6"
      :error="props.full_name_error"
      @update:model-value="handleInitialsInput"
    />
    <VTextField
      v-else
      :model-value="props.full_name"
      :readonly="readonly || props.isAuditParticipant || props.isPpuParticipant"
      class="flex-1-1-0 min-w-0"
      :class="{ 'invisible-readonly cursor-pointer': props.isAuditParticipant || props.isPpuParticipant }"
      label="ФИО"
      variant="outlined"
      persistent-placeholder
      :error="props.full_name_error"
      @update:model-value="val => updateField('full_name', val)"
      @click="(props.isAuditParticipant || props.isPpuParticipant) && employeeModalRef?.open()"
    />

    <DateMaskInput
      v-if="!props.isAuditParticipant && !props.isPpuParticipant && props.participant_type !== 'employee'"
      :model-value="props.birth_date ?? null"
      :disabled="readonly"
      density="comfortable"
      :max-date="new Date().toISOString().substring(0, 10)"
      class="flex-grow-1"
      :class="xs ? 'w-100' : 'max-width-250'"
      @update:model-value="val => updateField('birth_date', val || '')"
    >
      <template #label>
        <span>Дата рождения</span>
      </template>
    </DateMaskInput>

    <VTextField
      v-if="props.isAuditParticipant || props.isPpuParticipant || props.participant_type === 'employee'"
      :model-value="employeePosition"
      readonly
      class="flex-1-1-0 min-w-0"
      label="Должность"
      variant="outlined"
      persistent-placeholder
      :class="xs
        ? 'w-100'
        : (!props.isAuditParticipant && !props.isPpuParticipant && props.participant_type === 'employee' ? undefined : (props.isPpuParticipant ? undefined : 'max-width-250'))"
    />



    <VMaskInput
      v-if="!props.isAuditParticipant && !props.isPpuParticipant && props.participant_type !== 'employee'"
      :model-value="props.phone_number"
      :disabled="readonly"
      class="flex-grow-1"
      density="comfortable"
      hide-details
      label="Телефон"
      mask="+7 (###) ###-##-##"
      variant="outlined"
      persistent-placeholder
      :class="xs ? 'w-100' : 'max-width-250'"
      @update:model-value="val => updateField('phone_number', val)"
    />

    <VTextField
      v-if="!props.isAuditParticipant && !props.isPpuParticipant && props.participant_type === 'patient'"
      :model-value="props.medical_record_number"
      :disabled="readonly"
      class="flex-grow-1"
      label="№ ИБ/АК"
      variant="outlined"
      persistent-placeholder
      :class="xs ? 'w-100' : 'max-width-250'"
      :error="props.full_name_error"
      @update:model-value="val => updateField('medical_record_number', val)"
    />

    <template v-if="props.isAuditParticipant">
      <div class="flex-1-1-0 min-w-0">
        <div
          class="v-field v-field--active v-field--variant-outlined cursor-pointer audit-points-field"
          :class="{
            'v-field--error': hasCriterionError,
            'audit-points-field--collapsed': !isCellExpanded,
          }"
          @click="isCriteriaMenuOpen = true"
        >
          <div
            class="v-field__field"
            :class="isCellExpanded ? 'pa-3' : 'd-flex align-center px-4 overflow-hidden'"
          >
            <CollapsibleCell
              v-if="auditPoints.length"
              v-model:is-expanded="isCellExpanded"
              :items="auditPoints"
              type-label="предметов проверки"
              :collapsed-items-count="5"
            />
          </div>
          <div class="v-field__append-inner d-flex align-center h-100 ga-1 pe-3 pt-0">
            <VIcon
              v-if="auditPoints.length"
              icon="ri-close-line"
              size="18"
              class="text-medium-emphasis cursor-pointer"
              @click.stop="onClearCriteria"
            />
            <VIcon
              :icon="isCriteriaMenuOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'"
              size="20"
              class="text-medium-emphasis"
            />
          </div>
          <div class="v-field__outline">
            <div class="v-field__outline__start"></div>
            <div class="v-field__outline__notch">
              <label class="v-label v-field-label v-field-label--floating">Предмет проверки</label>
            </div>
            <div class="v-field__outline__end"></div>
          </div>
        </div>
      </div>

      <VDialog
        v-model="isCriteriaMenuOpen"
        max-width="1100"
        max-height="90vh"
        content-class="entity-form-menu"
      >
        <UniversalTreeMultiSelect
          field-key="audit_criterion"
          module="audit-criterion"
          :store="auditGroupStore"
          :only-leaves="true"
          collapsed-by-default
          :model-value="selectedCriteriaIds"
          @change="onPointsChange"
          @close="isCriteriaMenuOpen = false"
        />
      </VDialog>
    </template>

    <div
      v-if="!xs && (props.show_delete_button || canCopyPoints)"
      class="d-flex align-center ga-3 flex-grow-0 flex-shrink-0"
    >
      <VTooltip
        v-if="canCopyPoints"
        location="top"
        text="Скопировать предмет проверки во все поля"
      >
        <template #activator="{ props: tooltipProps }">
          <span v-bind="tooltipProps">
            <VIconBtn
              :rounded="false"
              :size="34"
              class="rounded-sm flex-grow-0 flex-shrink-0 copy-points-btn"
              :color="copyPointsBtnColor"
              icon="ri-checkbox-multiple-blank-line"
              variant="text"
              :disabled="!auditPoints.length"
              @click="copyPointsToOthers"
            />
          </span>
        </template>
      </VTooltip>
      <VIconBtn
        v-if="props.show_delete_button"
        :rounded="false"
        :size="34"
        class="rounded-sm flex-grow-0 flex-shrink-0"
        color="secondary"
        icon="ri-delete-bin-3-line"
        variant="text"
        @click="onDeleteClick"
      />
    </div>
  </div>

  <ConfirmDialog
    v-if="props.isAuditParticipant"
    v-model:is-dialog-visible="showDeleteConfirm"
    confirmation-question="Удалить участника?"
    :confirmation-text="deleteConfirmText"
    confirm-btn="Удалить"
    reject-btn="Отменить"
    @confirm="onDeleteConfirm"
  />

  <EmployeeSelectionModal
    v-if="props.isAuditParticipant"
    ref="employeeModalRef"
    :employees="apiStore.employees"
    @select="onEmployeeReplace"
  />
</template>

<style scoped lang="scss">
.max-width-250 {
  max-width: 250px !important;
}

/* Disabled: text-primary @ 30%, без глобального 0.45 у .v-icon-btn--disabled */
.copy-points-btn.v-icon-btn--disabled,
.copy-points-btn.v-btn--disabled {
  opacity: 1;
  color: rgb(var(--v-theme-text-primary), 0.3);
}

/* Контент не должен раздувать flex-basis — иначе «Предмет проверки» сжимает ФИО и должность */
.audit-points-field {
  min-width: 0;
  min-block-size: 48px;
}

.audit-points-field--collapsed {
  :deep(.cell-wrap) {
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    min-width: 0;
    overflow: hidden;
  }

  :deep(.cell-codes) {
    display: block;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  :deep(.cell-expand-btn) {
    flex-shrink: 0;
  }
}

:deep(.v-field.leader) {
  background: transparent !important;
  box-shadow: none !important;
}

/* убрать внутренние отступы у input-контейнера */
:deep(.v-field.leader .v-field__input) {
  padding: 0 !important;
}

:deep(.v-field.leader.v-field--error) {
  border: 1px solid rgb(var(--v-theme-error)) !important;
  border-radius: 6px;
}
</style>
